import React from 'react';
import { formatNumberWithCommas } from '@rocco/utils/format-number';
import { getPercentageColor } from '@rocco/utils/getPercentageColor';
import dayjs from 'dayjs';
import { DirectoryItemKPI } from './TileKPIContextMenu';
import { cn } from '@rocco/utils/cn';
import { useAtomValue } from 'jotai';
import { isSalesModeAtom } from '@rocco/states/apAdminUiContext';

import { ReactComponent as EmailIcon } from '@rocco/icons/svg/envelope-filled.svg';
import { ReactComponent as ReferralIcon } from '@rocco/icons/svg/globe.svg';

interface TileKPIProps {
    kpi: DirectoryItemKPI;
}

export const TileKPISummary = ({ kpi }: TileKPIProps) => {
    const { ClientType, ItemType } = kpi;

    const isSalesMode = useAtomValue(isSalesModeAtom);

    if (!isSalesMode) {
        return null;
    }

    const containerStyle = cn(ItemType === 'Project' ? 'pt-0' : 'pt-4');

    if (ClientType === 'PROFESSIONAL') {
        return (
            <div className={containerStyle}>
                <div className="flex cursor-default items-center justify-between gap-8">
                    {kpi.PeriodStart ? (
                        <span
                            className="text-s leading-normal text-gray-400"
                            title="When the client joined the platform"
                        >
                            {kpi.PeriodStart
                                ? dayjs(kpi.PeriodStart).format('MMM YYYY')
                                : ''}
                        </span>
                    ) : null}
                    <div className="flex items-center gap-2">
                        <EmailIcon className="h-3 w-3 opacity-75" />
                        <span
                            className="text-s flex items-center gap-2 font-bold leading-normal"
                            style={{
                                color: getPercentageColor(
                                    kpi.PeriodClientProjected12MonthEnquiries
                                        ? kpi.PeriodClientProjected12MonthEnquiries /
                                              31
                                        : 0,
                                    {
                                        lowThreshold: 0.25,
                                        midThreshold: 0.5,
                                        lowHue: 0,
                                        midHue: 90,
                                        highHue: 120,
                                    }
                                ),
                            }}
                            title="Average enquiries per year"
                        >
                            {formatNumberWithCommas(
                                kpi.PeriodClientProjected12MonthEnquiries || 0
                            )}
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <ReferralIcon className="h-3 w-3 opacity-75" />
                        <span
                            className="text-s font-bold leading-normal"
                            style={{
                                color: getPercentageColor(
                                    kpi.PeriodClientProjected12MonthReferrals
                                        ? kpi.PeriodClientProjected12MonthReferrals /
                                              500
                                        : 0,
                                    {
                                        lowThreshold: 0.2,
                                        midThreshold: 0.5,
                                        lowHue: 0,
                                        midHue: 90,
                                        highHue: 120,
                                    }
                                ),
                            }}
                            title="Average referrals per year"
                        >
                            {formatNumberWithCommas(
                                kpi.PeriodClientProjected12MonthReferrals || 0
                            )}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={containerStyle}>
            <div className="flex cursor-default items-center justify-between gap-8">
                {kpi.SubscriptionAmount ? (
                    <span
                        className="text-s leading-normal text-gray-400"
                        title="The client's current subscription value"
                    >
                        {kpi.SubscriptionAmount
                            ? `$${formatNumberWithCommas(kpi.SubscriptionAmount)}`
                            : null}
                    </span>
                ) : null}
                {kpi.PredictedWinRate ? (
                    <span
                        className="text-s font-bold leading-normal"
                        style={{
                            color: getPercentageColor(kpi.PredictedWinRate, {
                                lowThreshold: 50,
                                midThreshold: 75,
                                lowHue: 0,
                                midHue: 90,
                                highHue: 120,
                            }),
                        }}
                        title="The predicted chance of the client renewing"
                    >
                        {Math.round(kpi.PredictedWinRate)}%
                    </span>
                ) : null}
            </div>
        </div>
    );
};
